import {
  type JSX,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useInputChangeEvent } from '@haus-storefront-react/common-hooks/use-change-event'
import { formatPrice } from '@haus-storefront-react/common-utils'
import { Filters } from '@haus-storefront-react/filters'
import { DefaultFilterUrlStrategy } from '@haus-storefront-react/strategies'
import * as Slider from '@radix-ui/react-slider'
import { debounce } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { AriaLiveRegion, useWidgetReadyAfterInitialLoad, WidgetReadyBoundary } from '@components'
import type { PriceFilterAttributes } from './_propsFn'
import styles from './PriceFilter.module.scss'
import './PriceFilterSkeleton.css'

const ANNOUNCEMENT_TIMEOUT_MS = 1000
const SLIDER_DRAG_DEBOUNCE_MS = 250
const FILTERS_STRATEGIES = { filtersStoreStrategy: new DefaultFilterUrlStrategy() }

const clampNumber = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value))

const parseWholeNumber = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? Math.round(parsed) : undefined
}

const getMinorUnitFactor = (currencyCode?: string): number => {
  if (!currencyCode) {
    return 100
  }

  try {
    const digits = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).resolvedOptions().maximumFractionDigits
    return 10 ** (digits ?? 2)
  } catch {
    return 100
  }
}

const toMajorAmount = (
  rawAmount: number,
  minorUnitFactor: number,
  roundingMethod: 'floor' | 'ceil',
): number => Math[roundingMethod](rawAmount / minorUnitFactor)

const toRawAmount = (majorAmount: number, minorUnitFactor: number): number =>
  majorAmount * minorUnitFactor

type PriceRange = [number, number]

interface PriceFilterData {
  min: number
  max: number
  value: PriceRange
  setPriceFilter: (min: number, max: number) => void
  currencyCode?: string
}

interface PriceFilterContentProps {
  filterTitle: string
  minLabel: string
  maxLabel: string
  displayMode: PriceFilterAttributes['displayMode']
  priceFilterData: PriceFilterData
  minInputId: string
  maxInputId: string
  onAnnounce: (message: string) => void
}

interface PriceFilterWidgetContentProps {
  displayMode: PriceFilterAttributes['displayMode']
  enabledFilters: PriceFilterAttributes['enabledFilters']
  filtersContext: unknown
}

const hasSettledPriceFilterData = ({ min, max, value, currencyCode }: PriceFilterData): boolean => {
  const [selectedStart, selectedEnd] = value
  const hasFiniteBounds = Number.isFinite(min) && Number.isFinite(max)
  const hasFiniteSelection = Number.isFinite(selectedStart) && Number.isFinite(selectedEnd)

  if (!hasFiniteBounds || !hasFiniteSelection) {
    return false
  }

  const lowerBound = Math.min(min, max)
  const upperBound = Math.max(min, max)
  const isSelectionInBounds = selectedStart >= lowerBound && selectedEnd <= upperBound
  const hasRangeSpread = max > min
  const hasCurrencyCode = typeof currencyCode === 'string' && currencyCode.trim().length > 0

  return isSelectionInBounds && (hasRangeSpread || hasCurrencyCode)
}

const getFiltersReadyState = (value: unknown): { isLoading: boolean; isSettled: boolean } => {
  if (typeof value !== 'object' || value === null) {
    return { isLoading: true, isSettled: false }
  }

  const isLoadingValue = Reflect.get(value, 'isLoading')
  const facetValues = Reflect.get(value, 'facetValues')
  const initialFacetValues = Reflect.get(value, 'initialFacetValues')
  const initialPriceValues = Reflect.get(value, 'initialPriceValues')
  const isSettled =
    Array.isArray(facetValues) ||
    Array.isArray(initialFacetValues) ||
    initialPriceValues !== undefined
  const isLoading = typeof isLoadingValue === 'boolean' ? isLoadingValue : !isSettled

  return {
    isLoading,
    isSettled,
  }
}

/**
 * Render price filter controls from Filters.Price state.
 */
const PriceFilterContent = ({
  filterTitle,
  minLabel,
  maxLabel,
  displayMode,
  priceFilterData,
  minInputId,
  maxInputId,
  onAnnounce,
}: PriceFilterContentProps): JSX.Element => {
  const { t } = useTranslation()
  const { min: rawMin, max: rawMax, value, setPriceFilter, currencyCode } = priceFilterData
  const minorUnitFactor = useMemo(() => getMinorUnitFactor(currencyCode), [currencyCode])
  const [selectedStart, selectedEnd] = value
  const selectedRawMin = clampNumber(selectedStart, rawMin, rawMax)
  const selectedRawMax = clampNumber(selectedEnd, rawMin, rawMax)

  const min = toMajorAmount(rawMin, minorUnitFactor, 'floor')
  const max = toMajorAmount(rawMax, minorUnitFactor, 'ceil')
  const selectedMin = toMajorAmount(selectedRawMin, minorUnitFactor, 'floor')
  const selectedMax = toMajorAmount(selectedRawMax, minorUnitFactor, 'ceil')
  const effectiveRange: PriceRange =
    selectedMin === selectedMax && max > min ? [min, max] : [selectedMin, selectedMax]
  const [effectiveMin, effectiveMax] = effectiveRange
  const isSliderVisible = displayMode === 'both' || displayMode === 'slider'
  const isInputsVisible = displayMode === 'both' || displayMode === 'inputs'
  const sliderMinStepsBetweenThumbs = max > min ? 1 : 0
  const [minDraftValue, setMinDraftValue] = useState<string>(String(effectiveMin))
  const [maxDraftValue, setMaxDraftValue] = useState<string>(String(effectiveMax))
  const [sliderDraftRange, setSliderDraftRange] = useState<PriceRange>(effectiveRange)
  const sliderDraftRangeRef = useRef<PriceRange>(effectiveRange)
  const lastCommittedRangeRef = useRef<PriceRange>(effectiveRange)

  const syncSliderDraftRange = (nextRange: PriceRange): void => {
    const [nextMin, nextMax] = nextRange
    sliderDraftRangeRef.current = nextRange
    setSliderDraftRange(nextRange)
    setMinDraftValue(String(nextMin))
    setMaxDraftValue(String(nextMax))
  }

  useEffect(() => {
    const nextRange: PriceRange = [effectiveMin, effectiveMax]
    syncSliderDraftRange(nextRange)
    lastCommittedRangeRef.current = nextRange
  }, [effectiveMax, effectiveMin])

  const commitMajorRange = useCallback(
    (nextMin: number, nextMax: number, shouldAnnounce: boolean = true) => {
      const normalizedMin = clampNumber(nextMin, min, max)
      const normalizedMax = clampNumber(nextMax, min, max)
      const lowerBound = Math.round(Math.min(normalizedMin, normalizedMax))
      const upperBound = Math.round(Math.max(normalizedMin, normalizedMax))
      const lowerBoundRaw = toRawAmount(lowerBound, minorUnitFactor)
      const upperBoundRaw = toRawAmount(upperBound, minorUnitFactor)
      const formattedLowerBound = formatPrice(lowerBoundRaw, currencyCode)
      const formattedUpperBound = formatPrice(upperBoundRaw, currencyCode)

      setPriceFilter(lowerBoundRaw, upperBoundRaw)

      if (shouldAnnounce) {
        onAnnounce(
          t('a11y.priceFilter.ariaLive.rangeUpdated', {
            minPrice: formattedLowerBound,
            maxPrice: formattedUpperBound,
          }),
        )
      }
    },
    [currencyCode, max, min, minorUnitFactor, onAnnounce, setPriceFilter, t],
  )

  const commitDraftRange = (nextMinDraftValue: string, nextMaxDraftValue: string) => {
    const parsedMin = parseWholeNumber(nextMinDraftValue)
    const parsedMax = parseWholeNumber(nextMaxDraftValue)

    if (parsedMin === undefined || parsedMax === undefined) {
      setMinDraftValue(String(effectiveMin))
      setMaxDraftValue(String(effectiveMax))
      return
    }

    commitMajorRange(parsedMin, parsedMax)
  }

  const handleMinInputChange = useInputChangeEvent(setMinDraftValue)
  const handleMaxInputChange = useInputChangeEvent(setMaxDraftValue)
  const debouncedCommitSliderDragRange = useMemo(
    () =>
      debounce((nextMin: number, nextMax: number) => {
        commitMajorRange(nextMin, nextMax, false)
      }, SLIDER_DRAG_DEBOUNCE_MS),
    [commitMajorRange],
  )

  useEffect(() => {
    return () => {
      debouncedCommitSliderDragRange.cancel()
    }
  }, [debouncedCommitSliderDragRange])

  const finalizeSliderRange = () => {
    const nextCommittedRange = sliderDraftRangeRef.current
    const [committedMin, committedMax] = nextCommittedRange
    const [lastCommittedMin, lastCommittedMax] = lastCommittedRangeRef.current
    const isSameRange = lastCommittedMin === committedMin && lastCommittedMax === committedMax

    if (isSameRange) {
      return
    }

    debouncedCommitSliderDragRange.cancel()
    commitMajorRange(committedMin, committedMax)
    lastCommittedRangeRef.current = nextCommittedRange
  }

  const handleSliderChange = (sliderValue: number[]) => {
    const [nextMin, nextMax] = sliderValue
    if (nextMin === undefined || nextMax === undefined) {
      return
    }

    const nextRange: PriceRange = [nextMin, nextMax]
    syncSliderDraftRange(nextRange)
    debouncedCommitSliderDragRange(nextMin, nextMax)
  }

  const handleSliderCommit = (sliderValue: number[]) => {
    const [nextMin, nextMax] = sliderValue
    if (nextMin !== undefined && nextMax !== undefined) {
      syncSliderDraftRange([nextMin, nextMax])
    }

    finalizeSliderRange()
  }

  const handleInputBlur = () => {
    commitDraftRange(minDraftValue, maxDraftValue)
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    commitDraftRange(minDraftValue, maxDraftValue)
  }

  return (
    <section className={styles.container}>
      <fieldset className={styles.fieldset}>
        <legend className={styles.filterTitle}>{filterTitle}</legend>
        {isInputsVisible && (
          <div className={styles.inputs}>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor={minInputId}>
                <span>{minLabel}</span>
              </label>
              <input
                className={styles.input}
                id={minInputId}
                inputMode="numeric"
                max={effectiveMax}
                min={min}
                onChange={handleMinInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                step={1}
                type="number"
                value={minDraftValue}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label} htmlFor={maxInputId}>
                <span>{maxLabel}</span>
              </label>
              <input
                className={styles.input}
                id={maxInputId}
                inputMode="numeric"
                max={max}
                min={effectiveMin}
                onChange={handleMaxInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                step={1}
                type="number"
                value={maxDraftValue}
              />
            </div>
          </div>
        )}
        {isSliderVisible && (
          <div className={styles.sliderBlock}>
            <Slider.Root
              aria-label={filterTitle}
              className={styles.sliderRoot}
              max={max}
              min={min}
              minStepsBetweenThumbs={sliderMinStepsBetweenThumbs}
              onPointerUpCapture={finalizeSliderRange}
              onValueChange={handleSliderChange}
              onValueCommit={handleSliderCommit}
              step={1}
              value={sliderDraftRange}
            >
              <Slider.Track className={styles.sliderTrack}>
                <Slider.Range className={styles.sliderRange} />
              </Slider.Track>
              <Slider.Thumb aria-label={minLabel} className={styles.sliderThumb} />
              <Slider.Thumb aria-label={maxLabel} className={styles.sliderThumb} />
            </Slider.Root>
          </div>
        )}
        <p className={styles.currentRange}>
          {formatPrice(toRawAmount(sliderDraftRange[0], minorUnitFactor), currencyCode)} -{' '}
          {formatPrice(toRawAmount(sliderDraftRange[1], minorUnitFactor), currencyCode)}
        </p>
      </fieldset>
    </section>
  )
}

const PriceFilterWidgetContent = ({
  displayMode,
  enabledFilters,
  filtersContext,
}: PriceFilterWidgetContentProps): JSX.Element => {
  const { t } = useTranslation()
  const [announcement, setAnnouncement] = useState('')
  const minInputId = useId()
  const maxInputId = useId()
  const filterTitle = t('priceFilter.filterTitle')
  const minLabel = t('priceFilter.minLabel')
  const maxLabel = t('priceFilter.maxLabel')

  useEffect(() => {
    if (!announcement) {
      return
    }

    const timeoutId = window.setTimeout(() => setAnnouncement(''), ANNOUNCEMENT_TIMEOUT_MS)
    return () => window.clearTimeout(timeoutId)
  }, [announcement])

  const { isLoading, isSettled } = getFiltersReadyState(filtersContext)
  const hasPriceFilter =
    enabledFilters === undefined || enabledFilters.some((filter) => filter.type === 'price')
  const isReady = useWidgetReadyAfterInitialLoad({
    hasSettledContent: isSettled,
    hasVisibleContent: hasPriceFilter || isSettled,
    isLoading,
  })

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <AriaLiveRegion message={announcement} />
      <Filters.Price>
        {(priceFilterData) =>
          hasSettledPriceFilterData(priceFilterData) ? (
            <PriceFilterContent
              displayMode={displayMode}
              filterTitle={filterTitle}
              maxInputId={maxInputId}
              maxLabel={maxLabel}
              minInputId={minInputId}
              minLabel={minLabel}
              onAnnounce={setAnnouncement}
              priceFilterData={priceFilterData}
            />
          ) : null
        }
      </Filters.Price>
    </WidgetReadyBoundary>
  )
}

/**
 * Render a standalone price range filter tied to a product list identifier.
 */
export const PriceFilter = ({
  productListIdentifier,
  enabledFilters,
  displayMode,
}: PriceFilterAttributes): JSX.Element => {
  return (
    <Filters.Root
      enabledFilters={enabledFilters}
      productListIdentifier={productListIdentifier}
      strategies={FILTERS_STRATEGIES}
    >
      {(ctx) => (
        <PriceFilterWidgetContent
          displayMode={displayMode}
          enabledFilters={enabledFilters}
          filtersContext={ctx}
        />
      )}
    </Filters.Root>
  )
}

export default PriceFilter
